import React, { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import MenuBar from "@/components/MenuBar";
import LogTimeOffModal from "@/components/pto/LogTimeOffModal";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { Menu } from "lucide-react";
import { format } from "date-fns";
import { Leave } from "@/components/pto/types";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { isNannyUser } from "@/types/user";
import {
  convertFormToRequest,
  LeaveBalance,
  TimeOffFormValues,
} from "@/types/timeoff";

interface LeavePolicy {
  policyId: number;
  policyDisplayName: string;
  description: string;
  accrualPeriod: string;
  canHaveNegativeBalance: boolean;
  accrualStartFrom: string | null;
  requiresProof: boolean;
}

interface NannyTimeOffRequest {
  leaveTypeVersionId: number;
  startDate: string;
  endDate: string;
  hours?: number;
  reason?: string;
  submissionId?: number;
}

const NannyTimeOff: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("balances");
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);
  const [leavePolicies, setLeavePolicies] = useState<LeavePolicy[]>([]);
  const [leaveHistory, setLeaveHistory] = useState<Leave[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (user && isNannyUser(user)) {
      fetchInitialData();
    }
  }, [user]);

  const fetchInitialData = async () => {
    setIsLoading(true);
    if (!isNannyUser(user)) {
      setIsLoading(false);
      setLeaveBalances([]);
      setLeavePolicies([]);
      setLeaveHistory([]);
      return;
    }
    try {
      const [balancesRes, policiesRes, historyRes] = await Promise.all([
        ApiService.request<{ leavePolicies: LeaveBalance[] }>(
          EndpointKeys.FETCH_LEAVE_POLICIES,
        ),
        ApiService.request<{ leavePolicies: LeavePolicy[] }>(
          EndpointKeys.FETCH_LEAVE_POLICIES,
        ),
        ApiService.request<{ leaves: Leave[] }>(EndpointKeys.FETCH_LEAVES, {
          queryParams: {
            employeeId: user.employerEmployeeId,
          },
        }),
      ]);

      setLeaveBalances(balancesRes.leavePolicies);
      setLeavePolicies(policiesRes.leavePolicies);
      setLeaveHistory(historyRes.leaves);
    } catch (error) {
      console.error("Error fetching time off data:", error);
      toast({
        title: "Error",
        description: "Failed to fetch time off information",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimeOffSubmit = async (values: TimeOffFormValues) => {
    if (!user || !isNannyUser(user)) return;

    try {
      const request = convertFormToRequest(
        values,
        user.employerEmployeeId,
        true,
      );
      await ApiService.request(EndpointKeys.LOG_TIME_OFF, {
        method: "POST",
        body: request,
      });

      toast({
        title: "Success",
        description: "Time off request submitted for approval",
      });

      await fetchInitialData();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error submitting time off request:", error);
      toast({
        title: "Error",
        description: "Failed to submit time off request",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      PENDING: "secondary",
      APPROVED: "default",
      REJECTED: "destructive",
    };

    return (
      <Badge variant={variants[status] || "secondary"}>
        {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0 overflow-auto">
        <div className="p-4 md:p-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold">Time Off</h1>
            <Button onClick={() => setIsModalOpen(true)}>Request Time Off</Button>
          </div>

          <Card>
            <CardContent className="pt-6">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full justify-start overflow-auto">
                  <TabsTrigger value="balances">My Balances</TabsTrigger>
                  <TabsTrigger value="history">Request History</TabsTrigger>
                  <TabsTrigger value="policies">Leave Policies</TabsTrigger>
                </TabsList>

                <TabsContent value="balances">
                  <Card>
                    <CardHeader>
                      <h2 className="text-xl font-semibold">Leave Balances</h2>
                    </CardHeader>
                    <CardContent className="overflow-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Leave Type</TableHead>
                            <TableHead>Current Balance</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {leaveBalances.map((balance) => (
                            <TableRow key={balance.leaveTypeVersionId}>
                              <TableCell>{balance.policyDisplayName}</TableCell>
                              <TableCell>
                                {balance.balance < 999999
                                  ? Math.round(balance.balance * 100) / 100
                                  : "Unlimited"}{" "}
                                hours
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="history">
                  <Card>
                    <CardHeader>
                      <h2 className="text-xl font-semibold">Request History</h2>
                    </CardHeader>
                    <CardContent className="overflow-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Leave Type</TableHead>
                            <TableHead>Start Date</TableHead>
                            <TableHead>End Date</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Hours</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {leaveHistory.map((leave) => (
                            <TableRow key={leave.id}>
                              <TableCell>
                                {leave.leaveTypeVersion.displayName}
                              </TableCell>
                              <TableCell>
                                {format(new Date(leave.startDate), "PP")}
                              </TableCell>
                              <TableCell>
                                {format(new Date(leave.endDate), "PP")}
                              </TableCell>
                              <TableCell>
                                {getStatusBadge(leave.latestStatus)}
                              </TableCell>
                              <TableCell>{leave.hours || "N/A"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="policies">
                  <Card>
                    <CardHeader>
                      <h2 className="text-xl font-semibold">Leave Policies</h2>
                    </CardHeader>
                    <CardContent className="overflow-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Policy Name</TableHead>
                            <TableHead>Accrual Period</TableHead>
                            <TableHead>Negative Balance Allowed</TableHead>
                            <TableHead>Proof Required</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {leavePolicies.map((policy) => (
                            <TableRow key={policy.policyId}>
                              <TableCell>{policy.policyDisplayName}</TableCell>
                              <TableCell>{policy.accrualPeriod}</TableCell>
                              <TableCell>{policy.canHaveNegativeBalance ? "Yes" : "No"}</TableCell>
                              <TableCell>
                                {policy.requiresProof ? "Yes" : "No"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>

      <LogTimeOffModal
        leaveBalances={leaveBalances}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleTimeOffSubmit}
        requiresApproval={true}
      />
    </div>
  );
};

export default NannyTimeOff;
